import axios from 'axios';
import { API } from './API';
import { Store } from '../redux/store';
import { logout } from '../redux/slices/userslice';
import { resetGeolocation } from '../utils/geolocation';

const BROADCAST_CHANNEL_NAME = 'suprabhaatham_admin_single_tab_channel';

const api = axios.create({
  baseURL: API.BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Client-Type': 'admin_web',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Clears Redux state and notifies other tabs. No window.location redirect needed —
// ProtectedRoute re-evaluates isAuthenticated on the next React render and navigates
// to "/" via React Router, avoiding a full page reload that would restart the loop.
export const forceLogout = () => {
  Store.dispatch(logout({}));
  resetGeolocation();

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      ch.postMessage({ type: 'FORCED_LOGOUT' });
      ch.close();
    }
  } catch {
    // BroadcastChannel not supported — single-tab case, nothing to sync
  }
};

// Response interceptor: silent refresh on 401, immediate forced-logout on 403 blocked/revoked
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const msg: string = error.response?.data?.message || '';

    // Account blocked or session revoked by admin — refreshing won't help
    if (
      status === 403 &&
      (msg === 'Account blocked' || msg === 'Session revoked')
    ) {
      forceLogout();
      return Promise.reject(error);
    }

    // These endpoints return 401 for valid reasons (wrong credentials, wrong OTP).
    // A refresh attempt here would fail and mask the real error message.
    const url: string = originalRequest.url || '';
    const isCredentialEndpoint =
      url.includes('auth/login') || url.includes('auth/verify-otp');

    // Opt-out flag for non-critical calls (e.g. form/quiz response exports).
    // These STILL get the normal silent-refresh + retry (so a valid session
    // recovers transparently), but a FAILED refresh must never force a logout —
    // doing so previously caused an unable-to-login loop when a background
    // export 401'd.
    const skipAuthLogout = (originalRequest as any)?.skipAuthLogout === true;

    if (status === 401 && !originalRequest._retry && !isCredentialEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(
          API.BASE_URL + 'auth/refresh',
          {},
          { withCredentials: true }
        );
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Opt-out calls never end the session, even if the refresh failed.
        if (!skipAuthLogout) {
          forceLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
